<template>
  <div
    class="pg-select"
    :class="{
      'pg-select--open': isOpen && !disabled,
      'pg-select--eudora': variant === 'eudora',
      'pg-select--disabled': disabled,
    }"
    ref="wrapper"
  >
    <button
      type="button"
      class="pg-select__trigger"
      ref="trigger"
      :disabled="disabled"
      :aria-disabled="disabled ? 'true' : 'false'"
      @click="toggle"
    >
      <span class="pg-select__value">{{ selectedLabel || placeholder }}</span>
      <svg class="pg-select__arrow" :class="{ 'pg-select__arrow--up': isOpen && !openUpward, 'pg-select__arrow--menu-up': isOpen && openUpward }" width="12" height="8" viewBox="0 0 12 8" fill="none">
        <path d="M1 1l5 5 5-5" stroke="#333438" stroke-width="1.5" stroke-linecap="round" fill="none"/>
      </svg>
    </button>

    <div
      v-if="isOpen"
      class="pg-select__dropdown"
      :class="{ 'pg-select__dropdown--up': openUpward }"
      :style="dropdownFixedStyle"
    >
      <div
        v-for="option in options"
        :key="option.value"
        class="pg-select__option"
        :class="{ 'pg-select__option--selected': option.value === modelValue }"
        @click="select(option)"
      >
        {{ option.label }}
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CustomSelect',
  model: {
    prop: 'modelValue',
    event: 'change',
  },
  props: {
    modelValue: { default: null },
    options: { type: Array, default: () => [] }, // [{ value, label }]
    placeholder: { type: String, default: 'Selecione' },
    /** 'boticario' | 'eudora' — cor de foco e opção selecionada */
    variant: { type: String, default: 'boticario' },
    disabled: { type: Boolean, default: false },
  },
  data: () => ({
    isOpen: false,
    openUpward: false,
    dropdownFixedStyle: {},
  }),
  computed: {
    selectedLabel() {
      const opt = this.options.find(o => o.value === this.modelValue);
      return opt ? opt.label : '';
    },
  },
  watch: {
    disabled(d) {
      if (d) this.isOpen = false;
    },
    isOpen(open) {
      if (open) {
        this.$nextTick(() => {
          if (!this.isOpen) return;
          this.updateDropdownPlacement();
          window.addEventListener('scroll', this.onScrollOrResize, true);
          window.addEventListener('resize', this.onScrollOrResize);
        });
      } else {
        window.removeEventListener('scroll', this.onScrollOrResize, true);
        window.removeEventListener('resize', this.onScrollOrResize);
        this.openUpward = false;
        this.dropdownFixedStyle = {};
      }
    },
  },
  methods: {
    toggle() {
      if (this.disabled) return;
      this.isOpen = !this.isOpen;
    },
    onScrollOrResize() {
      if (!this.isOpen) return;
      if (this._placementRaf) cancelAnimationFrame(this._placementRaf);
      this._placementRaf = requestAnimationFrame(() => {
        this._placementRaf = null;
        this.updateDropdownPlacement();
      });
    },
    updateDropdownPlacement() {
      const trigger = this.$refs.trigger;
      if (!trigger) return;

      const MAX = 220;
      const GAP = 4;
      const EDGE = 8;
      const MIN_ROW = 44;
      const rect = trigger.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom - GAP;
      const spaceAbove = rect.top - GAP;
      const need = Math.min(MAX, Math.max(MIN_ROW, (this.options.length || 1) * MIN_ROW));

      let openUp = false;
      let maxH = MAX;

      if (spaceBelow >= need) {
        openUp = false;
        maxH = Math.min(MAX, Math.max(MIN_ROW, spaceBelow));
      } else if (spaceAbove >= need) {
        openUp = true;
        maxH = Math.min(MAX, Math.max(MIN_ROW, spaceAbove));
      } else if (spaceAbove > spaceBelow) {
        openUp = true;
        maxH = Math.min(MAX, Math.max(MIN_ROW, spaceAbove));
      } else {
        openUp = false;
        maxH = Math.min(MAX, Math.max(MIN_ROW, spaceBelow));
      }

      let left = rect.left;
      let width = rect.width;
      if (left + width > window.innerWidth - EDGE) {
        left = Math.max(EDGE, window.innerWidth - EDGE - width);
      }
      if (left < EDGE) left = EDGE;

      this.openUpward = openUp;

      if (!openUp) {
        this.dropdownFixedStyle = {
          position: 'fixed',
          top: `${rect.bottom + GAP}px`,
          bottom: 'auto',
          left: `${left}px`,
          width: `${width}px`,
          maxHeight: `${maxH}px`,
          zIndex: 10050,
        };
      } else {
        this.dropdownFixedStyle = {
          position: 'fixed',
          bottom: `${window.innerHeight - rect.top + GAP}px`,
          top: 'auto',
          left: `${left}px`,
          width: `${width}px`,
          maxHeight: `${maxH}px`,
          zIndex: 10050,
        };
      }
    },
    select(option) {
      this.$emit('change', option.value);
      this.isOpen = false;
    },
    onOutsideClick(e) {
      if (this.$refs.wrapper && !this.$refs.wrapper.contains(e.target)) {
        this.isOpen = false;
      }
    },
  },
  mounted() { document.addEventListener('click', this.onOutsideClick); },
  beforeDestroy() {
    document.removeEventListener('click', this.onOutsideClick);
    window.removeEventListener('scroll', this.onScrollOrResize, true);
    window.removeEventListener('resize', this.onScrollOrResize);
    if (this._placementRaf) cancelAnimationFrame(this._placementRaf);
  },
};
</script>

<style scoped>
.pg-select {
  position: relative;
  display: block;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  font-family: 'Inter', sans-serif;
  box-sizing: border-box;
  margin: 0;
  padding: 0;
  border: none;
  background: transparent;
  height: auto;
  box-shadow: none;
}

.pg-select__trigger {
  width: 100%;
  max-width: 100%;
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border: 1px solid #d0d0d0;
  border-radius: 12px;
  padding: 14px 16px;
  background: #fff;
  cursor: pointer;
  font-family: 'Inter', sans-serif;
  font-size: 0.9rem;
  color: #333438;
  text-align: left;
  transition: border-color 0.2s;
  appearance: none;
  -webkit-appearance: none;
  box-sizing: border-box;
}
.pg-select--open .pg-select__trigger,
.pg-select__trigger:focus {
  outline: none;
  border-color: #06a472;
}

.pg-select--disabled .pg-select__trigger {
  opacity: 0.55;
  cursor: not-allowed;
  pointer-events: none;
}

.pg-select__value {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.pg-select__arrow {
  flex-shrink: 0;
  margin-left: 8px;
  transition: transform 0.2s;
}
.pg-select__arrow--up {
  transform: rotate(180deg);
}
.pg-select__arrow--menu-up {
  transform: rotate(0deg);
}

.pg-select__dropdown {
  box-sizing: border-box;
  background: #fff;
  border: 1px solid #d0d0d0;
  border-radius: 12px;
  overflow-y: auto;
  overflow-x: hidden;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
}

.pg-select__dropdown--up {
  box-shadow: 0 -4px 16px rgba(0, 0, 0, 0.12);
}

.pg-select__option {
  padding: 12px 16px;
  font-family: 'Inter', sans-serif;
  font-size: 0.9rem;
  color: #333438;
  cursor: pointer;
  transition: background 0.15s;
}
.pg-select__option:first-child { border-radius: 12px 12px 0 0; }
.pg-select__option:last-child  { border-radius: 0 0 12px 12px; }
.pg-select__option:hover { background: #f5f5f5; }
.pg-select__option--selected {
  color: #06a472;
  font-weight: 600;
  background: #f0faf6;
}

.pg-select--eudora.pg-select--open .pg-select__trigger,
.pg-select--eudora .pg-select__trigger:focus {
  border-color: #613a53;
}
.pg-select--eudora .pg-select__option--selected {
  color: #613a53;
  background: rgba(97, 58, 83, 0.08);
}
</style>
